/**
 * unity.js
 * ---------------------------------------------------------------------------
 * Zehnte Schwesterdatei der Reihe: identische Logik, eigener Namensraum (un-).
 *
 * Erzeugt die Fähnchen und Luftballons im Hintergrund und lässt sie
 * umherziehen. Nähert sich der Mauszeiger auf weniger als CONFIG.fleeRadius
 * Pixel, stieben sie auseinander und beruhigen sich danach wieder auf ihr
 * Reisetempo. Flagge und Anstrahlung laufen rein über CSS.
 *
 * Zusätzlich verschiebt eine leichte Parallaxe Mond und Platz.
 * Kein Klick-Verhalten, keine Abhängigkeiten, kein Build.
 *
 * Startet nicht von selbst — einmal aufrufen:
 *
 *   import { initGermany } from "./germany.js";
 *   initGermany();
 *
 * Aktiviert wird jedes Element mit der Klasse .un-scene. Wird mindestens eine
 * gefunden, bekommt der body zusätzlich die Klasse .is-unity.
 */
export function germany() {
    "use strict";

    var CONFIG = {
        flags:        5,     // Anzahl Fähnchen (Desktop, mobil halbiert)
        balloons:     6,     // Anzahl Luftballons
        fleeRadius:   170,   // ab dieser Distanz zum Zeiger weichen sie aus (px)
        fleeForce:    1.1,   // Heftigkeit des Ausweichens
        maxSpeed:     3.6,   // Fluchttempo (px pro Frame bei 60 fps)
        flagSpeed:    0.55,  // Reisetempo Fähnchen
        balloonSpeed: 0.28,  // Reisetempo Luftballon
        wander:       0.05,  // Stärke der zufälligen Richtungswechsel
        calmDown:     0.012, // wie schnell der Schreck abklingt
        margin:       70,    // ab diesem Randabstand drehen sie ein
        flagSize:     [18, 32],
        balloonSize:  [16, 30]
    };

    // Kein Schwarz: ein schwarzer Luftballon liest sich als Trauerflor,
    // das ist der falsche Ton für einen Feiertag.
    var BALLOON_COLORS = ["#c8342d", "#f0c14b", "#e8e2d5", "#7a8aa0", "#d4703a"];

    var FLAG =
        '<span class="un-critter__body">' +
        '<i class="un-pole"></i>' +
        '<i class="un-strip"></i><i class="un-strip"></i>' +
        '<i class="un-strip"></i><i class="un-strip"></i>' +
        '</span>';

    var BALLOON = '<span class="un-critter__body"></span>';

    function reducedMotion() {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    function rand(min, max) { return min + Math.random() * (max - min); }
    function pick(list) { return list[Math.floor(Math.random() * list.length)]; }

    function create(scene) {
        var layer = scene.querySelector(".un-critters");
        if (!layer) return;

        var W = window.innerWidth;
        var H = window.innerHeight;
        var small = W < 700;
        var critters = [];
        var pointer = { x: -9999, y: -9999, active: false };
        var running = true;
        var last = 0;

        function spawn(kind, count) {
            var isFlag = kind === "flag";
            var range = isFlag ? CONFIG.flagSize : CONFIG.balloonSize;

            for (var i = 0; i < count; i++) {
                var size = rand(range[0], range[1]);
                var el = document.createElement("span");
                el.className = "un-critter un-critter--" + kind;
                el.innerHTML = isFlag ? FLAG : BALLOON;
                el.style.setProperty("--un-size", size.toFixed(1) + "px");
                if (!isFlag) el.style.setProperty("--un-balloon", pick(BALLOON_COLORS));
                el.style.opacity = rand(0.8, 1).toFixed(2);
                if (!isFlag) {
                    el.querySelector(".un-critter__body").style.animationDelay =
                        (-rand(0, 5.5)).toFixed(2) + "s, " + (-rand(0, 3.2)).toFixed(2) + "s";
                }
                layer.appendChild(el);

                var dir = rand(0, Math.PI * 2);
                var base = (isFlag ? CONFIG.flagSpeed : CONFIG.balloonSpeed) * rand(0.75, 1.3);

                critters.push({
                    el: el,
                    kind: kind,
                    w: size,
                    h: isFlag ? size / 1.5 : size / 0.8,
                    x: rand(0, W),
                    y: rand(0, H * 0.8),
                    vx: Math.cos(dir) * base,
                    vy: Math.sin(dir) * base,
                    base: base,
                    flee: 0,
                    face: 1,
                    fleeing: false
                });
            }
        }

        spawn("flag", small ? Math.ceil(CONFIG.flags / 2) : CONFIG.flags);
        spawn("balloon", small ? Math.ceil(CONFIG.balloons / 2) : CONFIG.balloons);

        function step(now) {
            if (!running) return;
            var dt = last ? Math.min((now - last) / 16.667, 3) : 1;
            last = now;

            for (var i = 0; i < critters.length; i++) {
                var c = critters[i];

                c.vx += rand(-1, 1) * CONFIG.wander * dt;
                c.vy += rand(-1, 1) * CONFIG.wander * dt;

                if (pointer.active) {
                    var dx = c.x - pointer.x;
                    var dy = c.y - pointer.y;
                    var d = Math.sqrt(dx * dx + dy * dy);
                    if (d < CONFIG.fleeRadius) {
                        var push = 1 - d / CONFIG.fleeRadius;
                        var inv = 1 / (d || 1);
                        c.vx += dx * inv * CONFIG.fleeForce * push * dt;
                        c.vy += dy * inv * CONFIG.fleeForce * push * dt;
                        c.flee = 1;
                    }
                }
                c.flee = Math.max(0, c.flee - CONFIG.calmDown * dt);

                if (c.x < CONFIG.margin)          c.vx += 0.07 * dt;
                else if (c.x > W - CONFIG.margin) c.vx -= 0.07 * dt;
                if (c.y < CONFIG.margin)          c.vy += 0.07 * dt;
                else if (c.y > H - CONFIG.margin) c.vy -= 0.07 * dt;

                var speed = Math.sqrt(c.vx * c.vx + c.vy * c.vy) || 0.0001;
                var target = c.base + (CONFIG.maxSpeed - c.base) * c.flee;
                var next = speed + (target - speed) * 0.05 * dt;
                c.vx = c.vx / speed * next;
                c.vy = c.vy / speed * next;

                c.x += c.vx * dt;
                c.y += c.vy * dt;
                if (c.x < -c.w) c.x = W + c.w;
                else if (c.x > W + c.w) c.x = -c.w;
                if (c.y < -c.h) c.y = H + c.h;
                else if (c.y > H + c.h) c.y = -c.h;

                c.el.style.transform =
                    "translate3d(" + (c.x - c.w / 2).toFixed(1) + "px," +
                    (c.y - c.h / 2).toFixed(1) + "px,0)";

                if (c.kind === "flag") {
                    var face = c.vx < 0 ? -1 : 1;
                    if (face !== c.face) {
                        c.face = face;
                        c.el.style.setProperty("--un-face", face);
                    }
                }

                var fleeing = c.flee > 0.2;
                if (fleeing !== c.fleeing) {
                    c.fleeing = fleeing;
                    c.el.classList.toggle("is-fleeing", fleeing);
                }
            }

            requestAnimationFrame(step);
        }

        window.addEventListener("pointermove", function (e) {
            pointer.x = e.clientX;
            pointer.y = e.clientY;
            pointer.active = true;
        }, { passive: true });

        document.addEventListener("pointerleave", function () {
            pointer.active = false;
        });

        window.addEventListener("resize", function () {
            W = window.innerWidth;
            H = window.innerHeight;
        }, { passive: true });

        document.addEventListener("visibilitychange", function () {
            if (document.hidden) {
                running = false;
            } else if (!running) {
                running = true;
                last = 0;
                requestAnimationFrame(step);
            }
        });

        requestAnimationFrame(step);
    }

    function init(scene) {
        if (scene.dataset.unReady) return;
        scene.dataset.unReady = "1";

        window.addEventListener("pointermove", function (e) {
            scene.style.setProperty("--un-px", (e.clientX / window.innerWidth * 2 - 1).toFixed(3));
            scene.style.setProperty("--un-py", (e.clientY / window.innerHeight * 2 - 1).toFixed(3));
        }, { passive: true });

        if (!reducedMotion()) create(scene);
    }

    function boot() {
        var scenes = document.querySelectorAll(".un-scene");
        if (!scenes.length) return;

        document.body.classList.add("is-germany");

        Array.prototype.forEach.call(scenes, init);
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", boot);
    } else {
        boot();
    }
}